// AI判定リザルト演出の段階判定（純粋関数）。境目・文言はここだけで調整する。

export type PointTier = 'normal' | 'good' | 'great' | 'epic' | 'legend'

/** 合計獲得ポイントの段階。上から順に判定する。 */
export const POINT_TIERS: readonly { min: number; tier: PointTier }[] = [
  { min: 300, tier: 'legend' },
  { min: 150, tier: 'epic' },
  { min: 60, tier: 'great' },
  { min: 20, tier: 'good' },
  { min: 0, tier: 'normal' },
]

export function pointTier(points: number): PointTier {
  return POINT_TIERS.find((t) => points >= t.min)?.tier ?? 'normal'
}

/** 合計ポイント表示のスタイル。Tailwind JIT が検出できるよう静的リテラルで持つ。burst は段階昇格時の光の輪。 */
export const POINT_TIER_STYLES: Record<PointTier, { className: string; textShadow?: string; burst: string }> = {
  normal: { className: 'text-3xl text-white', burst: 'border-white' },
  good: { className: 'text-3xl text-cyan-300', textShadow: '0 0 12px rgba(52,211,224,.5)', burst: 'border-cyan-300' },
  great: { className: 'text-3xl text-amber-300', textShadow: '0 0 18px rgba(245,185,66,.6)', burst: 'border-amber-300' },
  epic: { className: 'text-4xl text-purple-300 animate-pulse', textShadow: '0 0 24px rgba(176,107,240,.9)', burst: 'border-purple-400' },
  // 透明文字（background-clip）に text-shadow を付けると透けて濁るため drop-shadow で光らせる
  legend: { className: 'text-5xl rainbow-text rainbow-pulse drop-shadow-[0_0_14px_rgba(255,255,255,.45)]', burst: 'border-white' },
}

/** 段階の序列（段階アップ音の高さ等に使う） */
export const POINT_TIER_LEVEL: Record<PointTier, number> = { normal: 0, good: 1, great: 2, epic: 3, legend: 4 }

/** カウントアップの区間。to に到達した時点で tier になる。 */
export type CountUpSegment = { to: number; durationMs: number; tier: PointTier }

export const COUNTUP_TIMING = {
  SEGMENT_MS: 450, // 境目までの各区間
  LAST_MS: 600, // 最後の区間
  PLAIN_MS: 900, // 境目を1つも越えないとき（従来どおり）
} as const

/**
 * 0 → target を段階の境目で区切る。各区間は ease-out で境目手前に減速させ、
 * 越えた瞬間に昇格演出を出すために使う。境目ちょうどの target は最後の区間で昇格する。
 */
export function countUpSegments(target: number): CountUpSegment[] {
  const { SEGMENT_MS, LAST_MS, PLAIN_MS } = COUNTUP_TIMING
  const bounds = POINT_TIERS.map((t) => t.min)
    .filter((min) => min > 0 && min <= target)
    .sort((a, b) => a - b)
  if (bounds.length === 0) return [{ to: target, durationMs: PLAIN_MS, tier: pointTier(target) }]

  const segments: CountUpSegment[] = bounds.map((b) => ({ to: b, durationMs: SEGMENT_MS, tier: pointTier(b) }))
  const lastBound = bounds[bounds.length - 1]
  if (target > lastBound) {
    segments.push({ to: target, durationMs: LAST_MS, tier: pointTier(target) })
  } else {
    segments[segments.length - 1] = { ...segments[segments.length - 1], durationMs: LAST_MS }
  }
  return segments
}

export function countUpDurationMs(target: number): number {
  return countUpSegments(target).reduce((sum, s) => sum + s.durationMs, 0)
}

export type CutinTier = 'red' | 'gold' | 'rainbow'

/** カットインを出す最低 ai_rating */
export const CUTIN_MIN_RATING = 6

/** ai_rating 1つの段階（6〜7 赤 / 8〜9 金 / 10 虹）。6 未満なら null。 */
export function ratingTier(rating: number | null): CutinTier | null {
  const r = rating ?? 0
  if (r >= 10) return 'rainbow'
  if (r >= 8) return 'gold'
  if (r >= CUTIN_MIN_RATING) return 'red'
  return null
}

/** 判定した実績の最高 ai_rating からカットインの種類を決める。6 未満なら null（カットインなし）。 */
export function cutinTier(items: { ai_rating: number | null }[]): CutinTier | null {
  return ratingTier(items.reduce((m, { ai_rating }) => Math.max(m, ai_rating ?? 0), 0))
}

export const HIGHLIGHT_TIMING = {
  CARD_MS: 1000, // 1件ずつ叩きつける間隔
  HOLD_MS: 1500, // 最後に出てから判定完了までの間（1枚でもジャジャーンを鳴らし切れる長さ）
  MAX_CARDS: 3,
} as const

/**
 * カットイン後にハイライトする高評価実績（ai_rating 6 以上）。
 * ai_rating 降順 → 同評価は earned_points 降順で最大 MAX_CARDS 件、残りは件数だけ返す。
 */
export function highlightItems<T extends { ai_rating: number | null; earned_points: number }>(
  items: T[],
): { shown: T[]; rest: number } {
  const high = items
    .filter((i) => (i.ai_rating ?? 0) >= CUTIN_MIN_RATING)
    .sort((a, b) => (b.ai_rating ?? 0) - (a.ai_rating ?? 0) || b.earned_points - a.earned_points)
  const shown = high.slice(0, HIGHLIGHT_TIMING.MAX_CARDS)
  return { shown, rest: high.length - shown.length }
}

/** ハイライト全体の長さ（「ほか n件」も1枠として数える）。対象が無ければ 0。 */
export function highlightDurationMs(shownCount: number, rest: number): number {
  const { CARD_MS, HOLD_MS } = HIGHLIGHT_TIMING
  if (shownCount === 0) return 0
  return (shownCount + (rest > 0 ? 1 : 0)) * CARD_MS + HOLD_MS
}

export const CUTIN_MESSAGES: Record<CutinTier, string> = {
  red: 'すごい実績の気配…',
  gold: 'とんでもない実績の気配…！',
  rainbow: '伝説の気配――',
}

/** カットインの時間配分: 溜め（ゴゴゴ）→ 帯＋文言 → 昇格×回数 →（虹のみ）静止 → 閃光 */
export const CUTIN_TIMING = {
  RUMBLE_MS: 1200,
  BAND_MS: 800,
  UPGRADE_TEASE_MS: 600, // 昇格の発展（「……！？」、帯が暗くなり、ゴゴゴが静まる）
  UPGRADE_WINDUP_MS: 1200, // 昇格の溜め（3段階に等分：震え → 点滅 → 周りが暗く）
  UPGRADE_AFTER_MS: 1000, // 昇格（ダンッ）後の余韻
  HOLD_MS: 800, // 虹のみ：音も揺れも止めて溜める
  FLASH_MS: 400,
} as const

/** 昇格の溜めの段階数（UPGRADE_WINDUP_MS を等分する） */
export const CUTIN_WINDUP_STEPS = 3

/** 昇格1回ぶんの長さ（発展＋溜め＋余韻） */
export const CUTIN_UPGRADE_MS =
  CUTIN_TIMING.UPGRADE_TEASE_MS + CUTIN_TIMING.UPGRADE_WINDUP_MS + CUTIN_TIMING.UPGRADE_AFTER_MS

/** 昇格の発展〜溜めの間に帯へ出す文言 */
export const CUTIN_TEASE_MESSAGE = '……！？'

const CUTIN_ORDER: readonly CutinTier[] = ['red', 'gold', 'rainbow']

/**
 * カットインの色の並び。必ず赤から始まり、最終段階まで1段ずつ昇格する（確率なし）。
 * 例: gold → ['red', 'gold']、rainbow → ['red', 'gold', 'rainbow']
 */
export function cutinScenario(finalTier: CutinTier): CutinTier[] {
  return CUTIN_ORDER.slice(0, CUTIN_ORDER.indexOf(finalTier) + 1)
}

/** 揺れ・ゴゴゴ音が続く長さ（溜め＋帯＋昇格。虹の静止と閃光は含まない） */
export function cutinRumbleMs(finalTier: CutinTier): number {
  const { RUMBLE_MS, BAND_MS } = CUTIN_TIMING
  return RUMBLE_MS + BAND_MS + CUTIN_UPGRADE_MS * (cutinScenario(finalTier).length - 1)
}

/** カットイン全体の長さ（演出フェーズの待ち時間に使う） */
export function cutinDurationMs(finalTier: CutinTier): number {
  const { HOLD_MS, FLASH_MS } = CUTIN_TIMING
  return cutinRumbleMs(finalTier) + (finalTier === 'rainbow' ? HOLD_MS : 0) + FLASH_MS
}
